import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
    Plus, Search, Zap, Users, PlayCircle, PauseCircle,
    Trash2, Edit3, BarChart3, LayoutTemplate, Sparkles, Copy
} from 'lucide-react';
import CampaignTemplateGallery from '../components/workflow/CampaignTemplateGallery';
import VoiceButton from '../components/ui/VoiceButton';
import PageHeader from '../components/page/PageHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
    draft: { background: 'var(--surface)', color: 'var(--ink-2)' },
    active: { background: 'var(--good-bg)', color: 'var(--good)' },
    paused: { background: 'var(--warn-bg)', color: 'var(--warn)' },
    archived: { background: 'var(--surface)', color: 'var(--ink-label)' },
};

const TYPE_LABELS = {
    webinar_nurture: 'Webinar Nurture', cold_outbound: 'Cold Outbound',
    event_followup: 'Event Follow-up', event_marketing: 'Event Marketing', inbound_nurture: 'Inbound Nurture',
    onboarding: 'Onboarding', upsell: 'Upsell', re_engagement: 'Re-engagement', custom: 'Custom',
};

export default function Campaigns() {
    const { getAuthHeader, user } = useAuth();
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', campaign_type: 'custom', description: '' });
    const [creating, setCreating] = useState(false);
    const [showTemplates, setShowTemplates] = useState(false);
    const [createMode, setCreateMode] = useState('manual');

    const loadCampaigns = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/campaigns`, { headers: getAuthHeader() });
            setCampaigns(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch { toast.error('Failed to load campaigns'); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

    const handleCreate = async () => {
        if (!createForm.name.trim()) { toast.error('Campaign name required'); return; }
        setCreating(true);
        try {
            const res = await axios.post(`${API}/campaigns`, createForm, { headers: getAuthHeader() });
            toast.success('Campaign created');
            setShowCreate(false);
            if (createMode === 'ai' && createForm.description.trim()) {
                navigate(`/campaigns/${res.data.campaign_id}/edit?ai_prompt=${encodeURIComponent(createForm.description)}`);
            } else {
                navigate(`/campaigns/${res.data.campaign_id}/edit`);
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
        finally { setCreating(false); }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/campaigns/${id}`, { headers: getAuthHeader() });
            toast.success('Campaign deleted');
            loadCampaigns();
        } catch { toast.error('Failed to delete'); }
    };

    const handleDuplicate = async (id, name) => {
        try {
            const res = await axios.post(`${API}/campaigns/${id}/duplicate`, {}, { headers: getAuthHeader() });
            toast.success(`"${name}" duplicated`);
            loadCampaigns();
            navigate(`/campaigns/${res.data.campaign_id}/edit`);
        } catch { toast.error('Failed to duplicate'); }
    };

    const handleRename = async (id, newName) => {
        if (!newName?.trim()) return;
        try {
            await axios.put(`${API}/campaigns/${id}`, { name: newName.trim() }, { headers: getAuthHeader() });
            setCampaigns(prev => prev.map(c => c.campaign_id === id ? { ...c, name: newName.trim() } : c));
        } catch { toast.error('Failed to rename'); }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'paused' : 'active';
        try {
            await axios.put(`${API}/campaigns/${id}`, { status: newStatus }, { headers: getAuthHeader() });
            toast.success(`Campaign ${newStatus}`);
            loadCampaigns();
        } catch { toast.error('Failed to update status'); }
    };

    const filtered = campaigns.filter(c =>
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.campaign_type?.toLowerCase().includes(search.toLowerCase())
    );

    const totalEnrolled = campaigns.reduce((sum, c) => sum + (c.stats?.total_enrolled || 0), 0);
    const activeCount = campaigns.filter(c => c.status === 'active').length;

    if (loading) {
        return (
            <div className="space-y-6 max-w-[1600px]">
                <div className="grid grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="h-24 rounded-[14px] animate-pulse" style={{ background: 'var(--surface)', border: '1px solid var(--line)' }} />
                    ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="h-40 rounded-[14px] animate-pulse" style={{ background: 'var(--surface)', border: '1px solid var(--line)' }} />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-[1600px]">
            <PageHeader
                title="Campaigns"
                subtitle="Design and manage multi-step customer journeys"
                right={user?.is_admin ? (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => setShowTemplates(true)}>
                            <LayoutTemplate className="w-4 h-4 mr-1.5" /> Templates
                        </Button>
                        <Button onClick={() => setShowCreate(true)}>
                            <Plus className="w-4 h-4 mr-1.5" /> New Campaign
                        </Button>
                    </div>
                ) : undefined}
            />
            {/* KPI Cards */}
            <div className="grid grid-cols-3 gap-4">
                <div className="rounded-[14px] p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}>
                    <div className="flex items-center gap-2 mb-1">
                        <Zap className="w-4 h-4" style={{ color: 'var(--ink-2)' }} />
                        <span className="text-xs" style={{ color: 'var(--ink-label)' }}>Active Campaigns</span>
                    </div>
                    <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--ink-1)' }}>{activeCount}</span>
                </div>
                <div className="rounded-[14px] p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}>
                    <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4" style={{ color: 'var(--ink-2)' }} />
                        <span className="text-xs" style={{ color: 'var(--ink-label)' }}>Total Enrolled</span>
                    </div>
                    <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--ink-1)' }}>{totalEnrolled}</span>
                </div>
                <div className="rounded-[14px] p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}>
                    <div className="flex items-center gap-2 mb-1">
                        <BarChart3 className="w-4 h-4" style={{ color: 'var(--ink-2)' }} />
                        <span className="text-xs" style={{ color: 'var(--ink-label)' }}>Total Campaigns</span>
                    </div>
                    <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--ink-1)' }}>{campaigns.length}</span>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-2)' }} />
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search campaigns..."
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg focus:outline-none"
                    style={{ background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink-1)' }}
                />
            </div>

            {/* Campaign Grid */}
            {filtered.length === 0 ? (
                <div className="text-center py-16">
                    <Zap className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--ink-3)' }} />
                    <p style={{ color: 'var(--ink-label)' }}>No campaigns yet. Create your first one!</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(c => (
                        <div key={c.campaign_id} className="rounded-[14px] p-5 transition-shadow hover:shadow-md" style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}>
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1 min-w-0">
                                    <input
                                        defaultValue={c.name}
                                        onBlur={e => { if (e.target.value !== c.name) handleRename(c.campaign_id, e.target.value); }}
                                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.target.value = c.name; e.target.blur(); } }}
                                        className="text-sm font-semibold bg-transparent border-b border-transparent focus:outline-none transition-colors w-full truncate"
                                        style={{ color: 'var(--ink-1)', borderColor: 'transparent' }}
                                    />
                                    <span className="text-xs" style={{ color: 'var(--ink-label)' }}>{TYPE_LABELS[c.campaign_type] || c.campaign_type}</span>
                                </div>
                                <span className="text-xs px-2 py-0.5 rounded-full font-medium capitalize" style={STATUS_STYLES[c.status] || STATUS_STYLES.draft}>
                                    {c.status}
                                </span>
                            </div>
                            {c.description && <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--ink-2)' }}>{c.description}</p>}
                            <div className="flex items-center gap-4 text-xs mb-4" style={{ color: 'var(--ink-label)' }}>
                                <span>{c.steps?.length || 0} steps</span>
                                <span>{c.stats?.total_enrolled || 0} enrolled</span>
                                <span>{c.stats?.active || 0} active</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => navigate(`/campaigns/${c.campaign_id}/edit`)}>
                                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                                </Button>
                                {c.status !== 'archived' && (
                                    <Button variant="outline" size="sm" onClick={() => handleStatusToggle(c.campaign_id, c.status)}>
                                        {c.status === 'active' ? <><PauseCircle className="w-3.5 h-3.5 mr-1" /> Pause</> :
                                         <><PlayCircle className="w-3.5 h-3.5 mr-1" /> Activate</>}
                                    </Button>
                                )}
                                <Button variant="outline" size="sm" onClick={() => handleDuplicate(c.campaign_id, c.name)} title="Duplicate">
                                    <Copy className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(c.campaign_id, c.name)}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create Campaign</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {/* AI / Manual toggle */}
                        <div className="rounded-lg p-0.5 flex" style={{ background: 'var(--surface)' }}>
                            <button onClick={() => setCreateMode('manual')}
                                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${createMode === 'manual' ? 'shadow-sm' : ''}`}
                                style={createMode === 'manual' ? { background: 'var(--surface)', color: 'var(--ink-1)' } : { color: 'var(--ink-label)' }}>
                                Manual
                            </button>
                            <button onClick={() => setCreateMode('ai')}
                                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1 ${createMode === 'ai' ? 'shadow-sm' : ''}`}
                                style={createMode === 'ai' ? { background: 'var(--surface)', color: 'var(--ink-1)' } : { color: 'var(--ink-label)' }}>
                                <Sparkles className="w-3 h-3" /> AI-Assisted
                            </button>
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink-2)' }}>Campaign Name</label>
                            <input
                                value={createForm.name}
                                onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. Webinar Follow-up Series"
                                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none"
                                style={{ border: '1px solid var(--line)' }}
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink-2)' }}>Type</label>
                            <select
                                value={createForm.campaign_type}
                                onChange={e => setCreateForm(f => ({ ...f, campaign_type: e.target.value }))}
                                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none"
                                style={{ border: '1px solid var(--line)' }}
                            >
                                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                                    <option key={k} value={k}>{v}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink-2)' }}>
                                {createMode === 'ai' ? 'Describe your campaign' : 'Description (optional)'}
                            </label>
                            <div className="relative">
                                <textarea
                                    value={createForm.description}
                                    onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                                    rows={createMode === 'ai' ? 3 : 2}
                                    placeholder={createMode === 'ai' ? 'Describe your campaign and the AI will build it...' : ''}
                                    className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none"
                                    style={{ border: '1px solid var(--line)' }}
                                />
                                {createMode === 'ai' && (
                                    <div className="absolute right-2 top-2">
                                        <VoiceButton onTranscript={t => setCreateForm(f => ({ ...f, description: f.description ? `${f.description} ${t}` : t }))} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button onClick={handleCreate} disabled={creating}>
                            {creating ? 'Creating...' : 'Create Campaign'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Template Gallery */}
            <CampaignTemplateGallery open={showTemplates} onClose={() => setShowTemplates(false)} />
        </div>
    );
}
